import React, { useEffect, useRef, useState } from "react";
import { intl } from "LocaleWrapper";
import { useApolloClient, useMutation } from "@apollo/client";

import { Card, FormGroup, Select, Table } from "@cofano/cck";
import { Box, useTheme } from "@mui/material";
import { Add } from "@mui/icons-material";

import { useConfirm } from "components/hooks/useConfirm";
import { TableId, useBackendTable } from "components/table";

import { getCharterIcon } from "utils/charterUtils";
import { DossierLink } from "utils/LinkUtils";

import { Charter, Contract, Relation } from "types/index";
import { gql } from "__generated__/gql";
import { CharterSubtype, CharterType, RelationType, SortDirection } from "__generated__/enums";
import { usePermission } from "imports/Utils";

type CharterListProps = {
	contract: Pick<Contract, "id" | "charterType" | "charterSubtype" | "creditor" | "debtor" | "validFrom" | "validUntil" | "isParentContract">;
	refetch: () => void;
};

const CharterList = ({ contract, refetch }: CharterListProps) => {

	const confirm = useConfirm();
	const client = useApolloClient();

	const { isReadOnly, isRTR } = usePermission();
	const isSalesCharter = contract.charterType === CharterType.Sales;
	const isSpotCharter = contract.charterSubtype === CharterSubtype.Spot;
	const otherRelation = (isSalesCharter ? contract.debtor : contract.creditor) as Relation;

	const theme = useTheme();

	const getRelationTypeList = () => {
		const { creditor, debtor } = contract;

		const relationToCheck = isSalesCharter ? debtor : creditor;
		const res = [];
		if (relationToCheck?.customer) {
			res.push(RelationType.Customer);
		}
		if (relationToCheck?.operator) {
			res.push(RelationType.Operator);
		}
		if (relationToCheck?.isShipManager && !isSalesCharter) {
			res.push(RelationType.ShipManager);
		}
		if (relationToCheck?.skipper && !isSalesCharter) {
			res.push(RelationType.Skipper);
		}
		return res;
	};

	const atLeastTwoRelations = () => {

		const { creditor, debtor } = contract;

		if (isSalesCharter) {
			return !!(debtor?.operator && debtor?.customer);
		}

		const isOperator = !!creditor?.operator;
		const isCustomer = !!creditor?.customer;

		const exactOneOfEither = isOperator !== isCustomer;

		if (exactOneOfEither) {
			return !!(creditor?.isShipManager || creditor?.skipper);
		} else {
			return isCustomer;
		}
	};

	const relationTypes = getRelationTypeList();
	const hasTwoRelations = atLeastTwoRelations();

	const { tableProps } = useBackendTable({
		id: TableId.ContractCharters,
		onChange: async(pagination, sort, _, callback) => {
			try {
				const result = await client.query({
					query: CONTRACT_CHARTERS_QUERY,
					variables: {
						search: {
							contractId: contract.id,
							includeParentContractCharters: true,
						},
						pagination: {
							...pagination,
							sortList: sort ?? [ {
								field: "charterNumber",
								direction: SortDirection.Desc,
							} ],
						},
					},
				});

				callback({
					content: result.data.charters.content ?? [],
					totalResults: result.data.charters.totalElements,
				});
			} catch {
				callback({
					content: [],
					totalResults: 0,
				});
			}
		},
	});

	const [ relationType, setRelationType ] = useState(relationTypes[0] ? {
		id: relationTypes[0],
		name: intl.formatMessage({ id: relationTypes[0] }),
	} : null);

	const relationTypeRef = useRef(relationType);

	const [ saveCharter ] = useMutation(SAVE_CHARTER_MUTATION, { onCompleted: refetch });

	const createCharter = () => {
		saveCharter({
			variables: {
				input: {
					creditor: {
						id: contract.creditor?.id,
					},
					debtor: {
						id: contract.debtor?.id,
					},
					contract: {
						id: contract.id,
					},
					relationType: relationTypeRef.current?.id,
					type: contract.charterType,
					subtype: contract.charterSubtype,
					validFrom: contract.validFrom,
					validUntil: contract.validUntil,
				},
			},
		});
	};

	const onAddCharter = () => {
		confirm({
			type: "WARNING",
			title: intl.formatMessage({ id: "CONTRACT_CHARTER_ADD_PROMPT" }),
			content: hasTwoRelations ? (
				<FormGroup
					label={<span>{otherRelation.name} {intl.formatMessage({ id: "ROLE" })}</span>}
					overflow="hidden">
					<Select
						fetchOptions={relationTypes.map(it => ({
							id: it,
							name: intl.formatMessage({ id: it }),
						}))}
						value={relationType}
						onChange={(option) => setRelationType(option)}
						clearable={false}
						disableSearch />
				</FormGroup>
			) : <React.Fragment />,
		}).then(() => {
			createCharter();
		});
	};

	useEffect(() => {
		relationTypeRef.current = relationType;
	}, [ relationType ]);

	return (
		<Card
			title={intl.formatMessage({ id: isSalesCharter ? "SALES_CHARTERS" : "PURCHASE_CHARTERS" })}
			disablePadding
			actions={[
				{
					icon: <Add fontSize="small" />,
					onClick: onAddCharter,
					text: intl.formatMessage({ id: "ADD_BUTTON" }),
					hidden: isSpotCharter || !!contract.isParentContract || isReadOnly || !isRTR,
				},
			]}
		>
			<Box sx={{ backgroundColor: theme.palette.mode === "dark" ? "inherit" : "#F0F0F0" }}>
				<div className="contract-charters">
					<Table
						{...tableProps}
						columns={[ {
							id: "charterNumber",
							title: "",
							disableResizing: true,
							render: (row: Charter) =>
								<div style={{ alignItems: "center", display: "flex" }}>
									<span style={{ marginRight: 15, marginLeft: 5 }}>
										{getCharterIcon(row.type, row.subtype, "2x")}
									</span>
									<DossierLink charter={row as Charter} />
								</div>,
						},
						]}
					/>
				</div>
			</Box>
		</Card>
	);
};

const CONTRACT_CHARTERS_QUERY = gql(`#graphql
query getContractCharters($search: CharterSearch!, $pagination: Pagination!) {
	charters(search: $search, pagination: $pagination) {
		totalElements
		content {
			id
			type
			subtype
			charterNumber
		}
	}
}
`);

const SAVE_CHARTER_MUTATION = gql(`#graphql
mutation saveCharter($input: CharterInput!, $updateContract: Boolean = false) {
	saveCharter(input: $input, updateContract: $updateContract) {
		id
	}
}
`);

export default CharterList;
